import React from 'react';
import { FormProvider } from 'react-hook-form';
import { Pressable } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { ProfileEditForm } from '@/features/guardian-profile/components/profile-edit-form';
import { useGuardianProfileEdit } from '@/features/guardian-profile/hooks';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

export function ProfileEditPanel() {
  const { scale } = useScaling();
  const styles = useStyles();
  const navigation = useGuardianStackNavigation();
  const { form, submit, handlePickAvatar } = useGuardianProfileEdit();
  const canSave = form.formState.isDirty;

  return (
    <FormProvider {...form}>
      <Stack flex={1} style={styles.root}>
        <Stack direction="row" align="center" justify="space-between" style={styles.headingRow}>
          <Text variant="Font Guardian App/Heading/Heading H5 24px" style={styles.title}>
            {STRINGS.guardianProfile.settings.account}
          </Text>
          <Pressable
            accessibilityRole="button"
            disabled={!canSave}
            onPress={submit}
            style={({ pressed }) => [
              styles.saveButton,
              canSave ? styles.saveButtonEnabled : null,
              pressed ? styles.pressed : null,
            ]}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
              style={canSave ? styles.saveTextEnabled : styles.saveText}>
              {STRINGS.guardianProfile.edit.save}
            </Text>
          </Pressable>
        </Stack>
        <KeyboardAwareScrollView
          style={styles.form}
          contentContainerStyle={styles.formContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          bottomOffset={scale(12)}
          extraKeyboardSpace={scale(12)}>
          <ProfileEditForm
            tablet
            onPickAvatar={handlePickAvatar}
            onDeleteAccount={() => navigation.navigate('GuardianDeleteAccount')}
          />
        </KeyboardAwareScrollView>
      </Stack>
    </FormProvider>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: { paddingTop: scale(16), paddingHorizontal: scale(20) },
  headingRow: { height: scale(48) },
  title: { color: guardianColors['10'] },
  saveButton: {
    width: scale(100),
    height: scale(48),
    borderRadius: scale(12),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['2'],
    shadowColor: '#292757',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
    elevation: 1,
  },
  saveButtonEnabled: { backgroundColor: guardianColors['Main/Main'] },
  saveText: { color: guardianColors['4'] },
  saveTextEnabled: { color: colors['0'] },
  form: { flex: 1, marginTop: scale(20) },
  formContent: { paddingBottom: scale(100) },
  pressed: { opacity: 0.68 },
}));
